<template>
  <cs-preloader-overlay :loading="loading">
    <div>
      <div class="container-content">
        <div class="header">
          <a class="header-back-link" :title="$t('common.back')" @click="$router.push({ name: 'cabinet' })"></a>
          <div class="header-content">
            <div class="header-label">{{ $t('self.employed.turned.on') }}</div>
            <div class="header-title" data-private>
              {{ $t('inn.with.colon') }} {{ displayInnSelfEmployed }}
            </div>
          </div>
          <div class="button-link" @click="handleAutoDeclarationConnectShown">
            {{ $t('turn.off') }}
          </div>
        </div>
        <div class="content">
          <substrate-mount>
            <div class="title">{{ $t('common.autodeclaration') }}</div>
            <div
              v-for="project in preparedProjects"
              :key="project.projectId"
              class="auto-declaration-list"
            >
              <toggle-panel
                :title="project.name"
                :value="project.autoDeclaration"
                @click="handleChangeAutoDeclaration(project)"
              >
                <project-logo slot="icon" :project="project" />
                <capusta-input
                  slot="content"
                  :label="$t('product.description.default')"
                  :value="project.declarationDescription"
                  disabled
                >
                  <span
                    slot="link"
                    class="auto-declaration-link"
                    @click="handleDeclarationSettingsShown(project)"
                  >
                    {{ $t('common.change') }}
                  </span>
                </capusta-input>
              </toggle-panel>
            </div>
          </substrate-mount>
        </div>
      </div>

      <UnbindSelfEmployedModal
        v-model="isAutoDeclarationConnectShown"
        @unbind="handleUnbindSelfEmployed"
      />

      <AutoDeclarationSettingsModal
        v-model="isDeclarationSettingsShown"
        :project="selectedProject"
        @change="handleAutoDeclarationSettings"
      />
    </div>
  </cs-preloader-overlay>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';

import logrocket from '@/services/logrocket';

import SubstrateMount from '@/components/capusta/SubstrateMount';
import TogglePanel from 'molecules/panels/TogglePanel';
import ProjectLogo from 'molecules/project/ProjectLogo';
import CapustaInput from 'molecules/capusta-input/CapustaInput';

import UnbindSelfEmployedModal from './UnbindSelfEmployedModal';
import AutoDeclarationSettingsModal from './AutoDeclarationSettingsModal';

const DEFAULT_DECLARATION_DESCRIPTION = '';

export default {
  name: 'AutoDeclaration',
  components: {
    SubstrateMount,
    TogglePanel,
    ProjectLogo,
    CapustaInput,
    UnbindSelfEmployedModal,
    AutoDeclarationSettingsModal,
  },
  created() {
    this.GET_SELF_EMPLOYED_STATUS();
  },
  data() {
    return {
      loading: false,
      selectedProject: null,
      isAutoDeclarationConnectShown: false,
      isDeclarationSettingsShown: false,
    };
  },
  computed: {
    displayInnSelfEmployed() {
      if (!this.$validate.isObjectWithFields(this.SELF_EMPLOYED, ['properties'])) {
        return '';
      }

      if (this.$validate.isNull(this.SELF_EMPLOYED.properties)) {
        return '';
      }

      const { inn } = this.SELF_EMPLOYED.properties;

      if (this.$validate.isUndefined(inn)) {
        return '';
      }

      return inn;
    },
    preparedProjects() {
      return this.PROJECT_LIST
        .filter(project => project.type === 'PERSONAL')
        .map(project => {
          project.declarationDescription = this.getPreparedDescription(project);
          return project;
        });
    },
    ...mapState(['SELF_EMPLOYED']),
    ...mapGetters(['PROJECT_LIST']),
  },
  methods: {
    getPreparedDescription(project) {
      const { declarationDescription } = project;

      if (this.$validate.isNull(declarationDescription)) {
        return DEFAULT_DECLARATION_DESCRIPTION;
      }

      return declarationDescription;
    },
    async handleAutoDeclarationConnectShown() {
      this.isAutoDeclarationConnectShown = true;
    },
    handleDeclarationSettingsShown(project) {
      this.selectedProject = project;
      this.isDeclarationSettingsShown = true;
    },
    handleAutoDeclarationSettings(description) {
      if (this.$validate.isNull(this.selectedProject)) {
        return;
      }

      this.selectedProject.declarationDescription = description;
      this.selectedProject.autoDeclaration = true;

      this.CONNECT_AUTO_DECLARATION({
        projectId: this.selectedProject.projectId,
        description,
      });
    },
    handleChangeAutoDeclaration(project) {
      if (!project.autoDeclaration) {
        this.handleDeclarationSettingsShown(project);
      } else {
        this.disconnectAutoDeclaration(project);
      }
    },
    disconnectAutoDeclaration(project) {
      project.autoDeclaration = false;

      this.DISCONNECT_AUTO_DECLARATION(project.projectId);
    },
    async handleUnbindSelfEmployed() {
      this.loading = true;

      try {
        const { data: taxService } = await this.GET_SELF_EMPLOYED_TAX_SERVICE_STATUS();

        if (taxService.status !== 'AVAILABLE') {
          this.loading = false;

          this.$alert({
            type: 'warning',
            text: this.$t('fns.is.not.responding'),
          });
          return;
        }
      } catch (error) {
        this.loading = false;
        logrocket.logger(error);

        this.$alert({
          type: 'warning',
          text: this.$t('fns.is.not.responding'),
        });
        return;
      }

      try {
        const { data } = await this.SELF_EMPLOYED_DISCONNECT_REQUEST();

        if (!this.$validate.isObjectWithFields(data, ['inn', 'unbindDate'])) {
          this.loading = false;

          this.$alert({
            type: 'warning',
            text: this.$t('inn.not.exists'),
          });
          return;
        }

        this.$alert({
          type: 'sucess',
          text: this.$t('fns.will.not.know'),
        });
      } catch (error) {
        logrocket.logger(error);

        this.$alert({
          type: 'sucess',
          text: this.$t('server.error'),
        });
      }

      this.loading = false;
      this.GET_SELF_EMPLOYED_STATUS();
    },
    ...mapActions([
      'GET_SELF_EMPLOYED_STATUS',
      'CONNECT_AUTO_DECLARATION',
      'DISCONNECT_AUTO_DECLARATION',
      'SELF_EMPLOYED_DISCONNECT_REQUEST',
      'GET_SELF_EMPLOYED_TAX_SERVICE_STATUS',
    ]),
  },
};
</script>

<style lang="sass" scoped>
  .container-content
    width: 100%
    position: relative
    padding: 20px 0 50px

    +respond-to('small')
      width: 500px
      margin: 0 auto
      padding-top: 60px

  .header
    display: flex
    flex-direction: column
    position: relative
    margin-bottom: 20px
    padding: 0 13px

    +respond-to('small')
      flex-direction: row
      margin-bottom: 30px
      padding: 0

  .header-back-link
    position: absolute
    top: -12px
    width: 40px
    height: 40px
    border-radius: 50%
    background-image: url('~@/assets/img/arrow-left.svg')
    background-repeat: no-repeat
    background-position: center

    #{theme('eclipse')} &
      background-color: #cbe2b3

    #{theme('obsidian')} &
      background-color: #181722

    +respond-to('small-medium')
      position: static
      margin-left: -60px

    +respond-to('small')
      top: 0px
      width: 60px
      height: 60px

    &:hover
      background-color: #7CB342

  .header-content
    margin-bottom: 12px
    padding-left: 57px

    +respond-to('small')
      display: flex
      flex-direction: column
      justify-content: center
      margin-bottom: 0
      padding-left: 20px

      @media screen and (orientation: landscape) and (max-height: 480px)
        padding-left: 80px

  .header-label
    font-weight: 500
    font-size: 12px
    line-height: 22px
    color: #7CB342

    +respond-to('small')
      line-height: 22px

  .header-title
    display: inline-block
    padding-right: 10px
    align-self: center
    font-size: 20px
    line-height: 32px
    background-color: #1e1d2f

    +respond-to('small')
      margin-bottom: 0

    span
      display: block

      +respond-to('small')
        display: inline

  .form
    background-color: #181823

    +respond-to('small')
      border-radius: 10px

  .title
    margin-bottom: 20px
    font-size: 18px
    line-height: 22px
    color: #fff

  .auto-declaration-list

    .auto-declaration
      &:not(:last-child)
        margin-bottom: 18px

      +respond-to('small')
        &:not(:last-child)
          margin-bottom: 10px

  .auto-declaration
    padding: 20px
    background-color: #20212f
    box-shadow: 0px 0px 7px rgba(0, 0, 0, 0.15)

    +respond-to('small')
      border-radius: 5px

    &.active
      background-color: #27283c

  .auto-declaration-link
    font-weight: 500
    font-size: 14px
    line-height: 17px
    color: #9B51E0
    text-decoration: underline
    word-break: normal
    cursor: pointer

    &:hover
      text-decoration: none

  .button-link
    width: 196px
    margin: 0 auto
    padding: 10px 20px
    text-align: center
    border-radius: 50px
    font-weight: 500
    font-size: 14px
    line-height: 20px
    color: #fff
    background: #27283C
    box-shadow: 0px 0px 4px rgba(0, 0, 0, 0.15)
    cursor: pointer

    +respond-to('small')
      width: 144px
      position: absolute
      top: 50%
      right: 0
      transform: translateY(-50%)
</style>
